import { RestaurantResponseType } from '../restaurant/restaurant.types';
import { StationItemsMappingType } from '../restaurant/stations.types';
import { allergenData } from './allergen.types';
import { ChannelVisibilityType, ModifierOptionPriceType, RecordStatus } from './enums.types';
import { VisibilityOptionsType } from './item.types';
import { ModifierModifierOption } from './modifier.types';

export type FileType = File; // Define a type for the files
export const VALID_RATIOS = ['1x1', '16x9'];
export const Max_File_Size = 5 * 1024 * 1024; // 5MB
export const Max_Files_Upload_Limit = 100;
export const IMAGE_NAME_FORMAT = /^\d+-(1x1|16x9)(?:-(POS|ONLINE|KIOSK|THIRDPARTY))?\.(jpg|jpeg|png)$/i;
export const VALID_PRODUCT_NAME = ['POS', 'ONLINE', 'KIOSK', 'THIRDPARTY'];

export type FileWithValidation = {
  file: FileType;
  meta: {
    error?: string;
    parsedId?: number;
    ratio?: string;
    uniqueId?: string;
    deviceName?: string;
  };
};

export interface BulkImageUpdate {
  name: string;
  size: number;
  type: string;
  fileFor: string;
  fileUrl: string;
  thumbnailUrl: string;
  compressedFileUrl: string;
  aspectRatio?: string;
  fileStoreCategoryId: number;
}

export interface MenuSearchType extends VisibilityOptionsType {
  keywords?: string;
  allergens?: number[];
  tags?: number[];
  maximumPrice?: number;
  minimumPrice?: number;
  maximumPreparationTime?: number;
  categoryId?: number;
  menuId?: number;
  isFavorite?: string;
  isPopular?: string;
}

export interface MenuRequestType {
  menuName: string;
  menuDescription: string;
  restaurantIds: number[];
  startTime: Date;
  endTime: Date;
  picture: string;
}

export interface MenuResponseType {
  data: MenuDataResponse;
  message: string;
  error?: string | null;
}

export interface MenuDataResponse {
  restaurant?: RestaurantResponseType[];
  menuName: string;
  menuDescription: string;
  id: number;
  createdAt: Date;
  updatedAt: Date;
  startTime: Date;
  endTime: Date;
  picture: string;
}

export interface AllMenuResponseType {
  data: AllMenuDataType;
  message: string;
  error?: string | null;
}

export interface AllMenuDataType {
  id: number;
  restaurant?: RestaurantResponseType[];
  menuName: string;
  menuDescription: string;
  createdAt: Date;
  updatedAt: Date;
  categories?: Category[];
  genericModifiers?: ModifierGroup;
  tags?: Tag[];
  allergens?: allergenData[];
  startTime: Date;
  endTime: Date;
  picture: string;
}

export interface Category {
  id: number;
  categoryName: string;
  color: string;
  image: string;
  createdAt: Date;
  updatedAt: Date;
  subcategories?: Category[];
  items?: Item[];
  tag?: Tag[];
}

export interface Item {
  id: number;
  itemName: string;
  itemDescription: string;
  itemPrice: number;
  itemPicture: string;
  calories: number;
  category?: Category;
  quantity?: number;
  preparationTime?: string;
  stockStatus?: string;
  sortOrder?: number;
  showOnMenu?: boolean;
  showOnline?: boolean;
  showPOS?: boolean;
  showQR?: boolean;
  showThirdParty?: boolean;
  posDisplayName?: string;
  kdsName?: string;
  createdAt?: Date;
  updatedAt?: Date;
  modifierGroup?: ModifierGroup[];
  modifiers?: IModifier[];
  stationItemsMapping?: StationItemsMappingType[];
  tag?: Tag[];
  startTime: string;
  endTime: string;
  discountPercentage?: number;
}

export interface Tag {
  id: number;
  tagName: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Allergen {
  id: number;
  allergenName: string;
  createdAt: Date;
  updatedAt: Date;
}
export interface ModifierGroup {
  id?: number;
  groupName?: string;
  isOptional?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  parentModifierGroupId?: number;
  description?: string;
  prefix?: string;
  maxSelector?: number;
  minSelector?: number;
  priority?: number;
  multiSelect?: boolean;
  isGeneric?: boolean;
  subgroups?: ModifierGroup[];
  modifiers?: IModifier[];
  nestedModifiers?: IModifier[];
  modifierOption?: ModifierOptions[];
  modifierGroupName?: string;
  isNested?: boolean;
  modifierName?: string;
  modifierGroupId?: number;
  type?: string;
}

export interface ModifierOptions {
  id?: number;
  modifier_optionid?: number;
  price?: number;
  optionName?: string;
  optionDisplayName?: string;
  totalPrice?: number | null | undefined;
  overridePrice?: number | null | undefined;
  orderedQuantity?: number;
  canOverridePrice?: boolean;
  isDefaultSelected?: boolean;
  modifierName?: string;
  minSelector?: number;
  maxSelector?: number;
  isOptional?: boolean;
  nestedModifiers?: NestedModifiers[];
  modifierOption?: ModifierOptions[];
  limit?: number;
  stockStatus?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  isGeneric?: boolean;
  isNested?: boolean;
  addNested?: boolean;
  isSizeModifier?: boolean;
  isStockAvailable?: boolean;
  maxLimit?: number;
  pizzaSide?: string;
  originalPrice?: number;
  isLoyaltyApplied?: boolean;
  noMaxSelection?: boolean;
}

export interface IModifier {
  id: number;
  modifierName: string;
  isOptional: boolean;
  limit: number;
  price: number;
  stockStatus: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  isGeneric?: boolean;
  multiSelect?: boolean;
  subgroups?: IModifier[];
  modifierGroupId?: number;
  orderedQuantity?: number;
  modifierOption?: ModifierOptions[];
  nestedModifiers?: NestedModifiers[];
  optionName?: string;
  canGuestSelectMoreModifiers?: boolean;
  groupName?: string;
  description?: string;
  priority?: number;
  prefix?: string;
  maxSelector?: number;
  minSelector?: number;
  isNested?: boolean;
  addNested?: boolean;
  isSizeModifier?: boolean;
  maxLimit?: number;
  isDefaultSelected?: boolean;
  isStockAvailable?: boolean;
  pizzaSelection?: boolean;
  noMaxSelection?: boolean;
  isReqModMissing?: boolean;
}
export interface NestedModifiers {
  id: number;
  modifierName: string;
  isOptional: boolean;
  limit: number;
  price: number;
  stockStatus: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  isGeneric?: boolean;
  multiSelect?: boolean;
  subgroups?: IModifier[];
  modifierGroupId?: number;
  orderedQuantity?: number;
  modifierOption?: ModifierOptions[];
  optionName?: string;
  canGuestSelectMoreModifiers?: boolean;
  groupName?: string;
  description?: string;
  priority?: number;
  prefix?: string;
  maxSelector?: number;
  minSelector?: number;
  isNested?: boolean;
  modifierModifierOptions?: ModifierModifierOption[];
}

export interface CartModifierOption {
  id?: number;
  optionName?: string;
  optionDisplayName?: string;
  overridePrice?: number | undefined | null;
  price?: number; // used in item size
  totalPrice?: number | undefined | null; // Used in item size
  isSizeModifier?: boolean;
  canOverridePrice?: boolean;
  isStockAvailable?: boolean;
  isDefaultSelected?: boolean;
  orderedQuantity?: number;
  isSelected?: boolean;
  pizzaSide?: string;
}
export interface CartModifier {
  id: number;
  modifierName: string;
  isOptional: boolean;
  limit?: number; // TODO: add optional while resolving conflict
  price?: number; // TODO: add optional while resolving conflict
  orderedQuantity?: number;
  stockStatus?: boolean; // TODO: add optional while resolving conflict
  createdAt?: Date;
  updatedAt?: Date;
  isGeneric?: boolean;
  multiSelect?: boolean;
  nestedModifiers?: CartModifier[];
  modifierOption?: CartModifierOption[];
  modifierGroupId?: number;
  addNested?: boolean;
  modifierOptionPriceType?: ModifierOptionPriceType;
}
export interface CartModifierGroup {
  id: number;
  modifierName: string;
  type: ModifierGroupType;
  isNested: boolean;
  isGeneric: false;
  modifiers: CartModifier[];
  isOptional: boolean;
  showOnline: boolean;
  description: string;
  modifierGroupId: number;
  modifierGroupName: string;
  groupName?: string;
  minSelector?: number;
}

export interface TicketModifiersMetadata {
  modifierName: string;
  modifierNameWithComma: string;
  price: string;
  isMultiplierModifier: boolean;
  multiplierModifierQuantity: number;
  pizzaSide?: string;
}

export enum ModifierGroupType {
  GROUP = 'group',
  MODIFIER = 'modifier',
}
export interface MenuI {
  id: number;
  menuName: string;
  posName?: string;
  buttonColor: string;
  image?: string;
  sortOrder: number;
  createdAt?: Date;
  updatedAt?: Date;
}

export type AllMenuListResponseType = Omit<AllMenuResponseType, 'data'> & {
  data: MenuDataResponse[];
};

export interface KioskMenuResponseType {
  data: KioskMenuDataType;
  message: string;
  error?: string | null;
}
export interface KioskMenuDataType {
  id: number;
  name: string;
  address: string;
  onlineOrder: boolean;
  autoFire: boolean;
  autoFireTimer: string;
  onlineOrderTimer: string;
  latitude: number;
  longitude: number;
  createdAt: Date;
  updatedAt: Date;
  menus: AllMenuDataType[];
}

export enum FileStoreStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum FileStoreAspectRatio {
  ORIGINAL = 'Original',
  ONEBYONE = '1:1',
  SIXTEENBYNINE = '16:9',
}

export enum FileForType {
  POS = 'pos',
  KDS = 'kds',
  KIOSK = 'kiosk',
  ONLINE = 'online',
  BRANDBOOK = 'brandBook',
  MENU = 'menu',
  EMPLOYEE = 'employee',
  LOCATION = 'location',
  MENU_BOARD = 'menuBoard',
  THIRD_PARTY = 'thirdparty',
  GIFT_CARD = 'giftcard',
}

// Dashboard
export interface IMenu {
  id?: number;
  menuName: string;
  posDisplayName?: string | undefined;
  posButtonColor: string;
  picture?: string;
  sortOrder?: number;
  status?: RecordStatus;
  settingId?: number | null;
  restaurantId?: number;
  // createdAt?: string;
  // updatedAt?: string;
  // deletedAt?: string;
  createdBy?: string | null;
  updatedBy?: string | null;
  deletedBy?: string | null;
}
export interface IMenuResponse {
  menus: IMenu[];
  totalCount: number;
}

export interface IMenuCategory {
  id?: number;
  label: number;
  value: number;
}
export interface IMenuItemResponse {
  menu: IMenu;
  categories?: IMenuCategory[];
}
export interface IGetMenuIdResponse {
  menu: IMenu;
  categories?: IMenuCategory[] | [];
  onPrem?: IVisibilitySetting;
  offPrem?: IVisibilitySetting;
  custom?: IVisibilitySetting[];
}
export interface IScheduling {
  id: number;
  day: string;
  allDay: boolean | string;
  startTime: string | null;
  endTime: string | null;
}
export interface IVisibilitySetting {
  isVisible: boolean;
  isDefault: boolean;
  channelVisibilityType: ChannelVisibilityType;
  device: string;
  scheduling: IScheduling[];
}

export type MenuFilter = Partial<IMenu>;
export interface IMenuUpdate {
  id?: number;
  menuName: string;
  posDisplayName?: string | undefined;
  posButtonColor: string;
  picture?: string;
  sortOrder?: number;
  status?: RecordStatus;
  setting?: number;
  restaurantId?: number;
  // createdAt?: string;
  // updatedAt?: string;
  // deletedAt?: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  deletedBy?: number | null;
}

export interface ItemImageRecord {
  id: number;
  kioskItemImage?: string | null;
  itemPicture?: string | null;
  onlineImage?: string | null;
  thirdPartyImage?: string | null;
  landscapeImage?: string | null;
}
export enum ImageCategory {
  POS = 'pos',
  MPOS = 'mpos',
  ONLINE = 'online',
  QR = 'qr',
  KIOSK = 'kiosk',
  THIRD_PARTY = 'thirdparty',
  ALL = 'all',
}

export interface ISettingRow {
  id: number;
  type: string;
  status: string;
}

export interface IVisibilitySettingRow {
  id: number;
  device: string;
  status: string;
  isDefault: boolean;
  isVisible: boolean;
  settingId: number;
  channelVisibilityType: string;
}

export interface IDaySchedule {
  id: number;
  day: string;
  status: string;
  endTime: string;
  startTime: string;
  availabilityType: string;
  visibilitySettingId: number;
}
